import useSafeState from "../utils/safe-state";
import { Button, Col, DatePicker, Form, InputNumber, Row, Select, Spin } from "antd";
import React, { useEffect } from "react";
import { Trans } from "@lingui/macro";
import { useAxios } from "../utils/hooks";
import { openNotification } from "../utils/openNotification";
import moment from "moment";

interface Interface {
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const LegacyStockDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = { headers: { 'Content-Type': 'application/json' } };
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);

    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();

        if (props.mode === 'Edit' && data && data.id) {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if (key === 'validTo') {
                    fieldsValues.push({ name: key, value: value ? moment(value as string) : null });
                } else if (key === 'item' && value && typeof value === 'object') {
                    fieldsValues.push({ name: ['item', 'id'], value: (value as any).id });
                } else {
                    fieldsValues.push({ name: key, value: value });
                }
            }
            form.setFields(fieldsValues);
        }
    }, [props.mode]);

    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .post('/resource/items/page', {
                    start: 0,
                    length: 1000,
                    direction: 'asc',
                    sorted_field: 'id',
                    query: {
                    }
                })
                .then(response => {
                    const items = (response.data && response.data.data) || [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error);
                    openNotification('error', 'Failed to load items');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        } else {
            setItemOptions([]);
        }
    };


    const handleEdit = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .put(
                    '/resource/legacy-stock/' + encodeURIComponent(data.id),
                    {
                        id: data.id,
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        validTo: values.validTo ? moment(values.validTo).format('YYYY-MM-DD HH:mm') : null,
                        quantity: values.quantity,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Legacy Stock successfully updated.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .post(
                    '/resource/legacy-stock',
                    {
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        validTo: values.validTo ? moment(values.validTo).format('YYYY-MM-DD HH:mm') : null,
                        quantity: values.quantity
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Legacy Stock successfully created.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Form
            id="newLegacyStock"
            layout='vertical'
            form={form}
            onFinish={(values) => (props.mode === 'Edit' ? handleEdit(values) : handleFinish(values))}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24} style={{marginBottom: 20}}><Col span={12}><span style={{color: "red", fontStyle: "italic"}}>* Mandantory fields</span></Col></Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['item', 'id']}
                            label={<Trans>Item</Trans>}
                            rules={[{ required: true, message: <Trans>Please select an item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                                disabled={props.mode === 'Edit'}
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="validTo"
                            label={<Trans>Valid To</Trans>}
                            rules={[{ required: true, message: <Trans>Please select a valid to date</Trans> }]}
                        >
                            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="quantity"
                            label={<Trans>MSC product relevant quantity</Trans>}
                        >
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div>
                {props.mode === 'Edit'
                    ? (<Button htmlType="submit" type="primary">
                        <Trans>Save</Trans>
                    </Button>)
                    :
                    <>
                        <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                            <Trans>Clear</Trans>
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            style={{ marginRight: 8 }}
                        >
                            <Trans>Save</Trans>
                        </Button>
                    </>
                }
            </div>
        </Form>
    );

}
export default LegacyStockDrawer;

